/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Checkbox, Form, Input, SecretInput } from 'components/forms'
import Link from 'components/link'
import MailcheckedInput from 'components/mailchecked-input'
import { InfoButton } from 'components/buttons'
import Image from 'components/image'
import { isString } from 'lodash'
import { useMediaQuery, useTheme } from '@material-ui/core'
import { privacyPolicyPath, termsOfUsePath } from 'lib/urls'
import splitName from 'lib/split-name'
import { email } from 'lib/form-validation'
import { FieldError, useForm } from 'react-hook-form'
import {
  SET_UNIVERSAL_NOTIFICATION_MODAL_CONFIG_EVENT,
  SHOW_UNIVERSAL_NOTIFICATION_MODAL_EVENT,
} from 'shared/constants'
import { track } from 'lib/analytics'
import { bowserFlags } from 'lib/bowser'
import styles from './signup-form.styles.scss'
import GoogleSignInButton from '../../../../../LoginPage/components/GoogleSignInButton/GoogleSignInButton'
import SquiggleDivider from '../../../../../LoginPage/components/SquiggleDivider'
import InfoModal from '../../../../../LoginPage/components/InfoModal'
import AppleSignInButton from '../../../../../LoginPage/components/AppleSignInButton'

export interface SignupSuccessResponse {
  registry: any
  user: any
}

export type SignupFunction = (
  data: {
    formData: SignupFormFields
    recaptchaToken?: string
  },
  trackingData: SignupFormTrackingData
) => Promise<SignupSuccessResponse>

interface SignupFormProps {
  children?: React.ReactNode
  onSubmit: SignupFunction
  onSubmitSuccess: (result: SignupSuccessResponse) => void
  submitButtonText?: string
  initialValues: {
    name?: string
    email?: string
    password?: string
  }
  setHeaderState: (state: any) => void
  signedUp?: boolean
  ssoEnabled?: boolean
}

interface SignupFormFields {
  firstName: string
  lastName: string
  email: string
  password: string
  consentedToPromotionalOffers: boolean
}

interface SignupFormTrackingData {
  eventLocation: string
  userAuthenticationMethod: string
}

// eslint-disable-next-line complexity
export const SignupForm = ({
  children,
  onSubmit,
  onSubmitSuccess,
  initialValues,
  setHeaderState,
  signedUp = false,
  submitButtonText = 'Sign Up',
  ssoEnabled = false,
}: SignupFormProps) => {
  const defaultValues = {
    firstName: splitName(initialValues.name).firstName,
    lastName: splitName(initialValues.name).lastName,
    email: initialValues.email,
    password: initialValues.password,
    consentedToPromotionalOffers: false,
  }
  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error('Execute recaptcha not yet available')
      return
    }

    return await executeRecaptcha('signup')
  }, [executeRecaptcha])

  const {
    errors,
    register,
    setValue,
    triggerValidation,
    watch,
    handleSubmit: rhfHandleSubmit,
  } = useForm<SignupFormFields>({ defaultValues })
  const [submitting, setSubmitting] = useState(false)
  const [formSubmissionError, setFormSubmissionError] = useState<string>()
  const [validatePasswordLive, setValidatePasswordLive] = useState(false)
  const password = watch('password')

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [ssoSignupState, setSsoSignupState] = useState({
    authenticationCompleted: false,
    email: '',
    firstName: '',
    isPrivateEmail: false,
    lastName: '',
    provider: '',
  })

  const appleSsoEnabled = bowserFlags.ios && !bowserFlags.ipad

  const generateErrorMessage = (field: string, error: FieldError) => {
    if (error.message) {
      return error.message
    }

    if (error.type === 'required') {
      return `Enter your ${field}`
    }
    return `${field} is invalid`
  }

  const generatePasswordErrorMessage = (type: string) => {
    if (type === 'required') {
      return (
        <span className={styles.SignupForm__helpOrErrorMessage}>
          <Image
            alt="Warning Triangle"
            height={18}
            src="https://images.babylist.com/image/upload/f_auto,q_auto/v1694022585/Onboarding/triangle_danger.png"
            width={18}
          />
          Password must be at least 8 characters
        </span>
      )
    }
    if (type === 'minLength') {
      return (
        <span className={styles.SignupForm__helpOrErrorMessage}>
          <Image
            alt="Warning Triangle"
            height={18}
            src="https://images.babylist.com/image/upload/f_auto,q_auto/v1694022585/Onboarding/triangle_danger.png"
            width={18}
          />
          Minimum length is 8 characters
        </span>
      )
    }
    // This shouldn't ever happen
    return 'Password is invalid'
  }

  const generatePasswordHelpText = () => {
    if (validatePasswordLive) {
      return (
        <span className={styles.SignupForm__helpOrErrorMessage}>
          <Image
            alt="Circle Checkmark"
            height={18}
            src="https://images.babylist.com/image/upload/f_auto,q_auto/v1694022585/Onboarding/circle_check.png"
            width={18}
          />
          Password requirements met
        </span>
      )
    }
    return 'Passwords must be at least 8 characters'
  }

  const handlePasswordOnBlur = () => {
    if (!validatePasswordLive && password) {
      triggerValidation('password')
      setValidatePasswordLive(true)
    }
  }

  const handleEmailChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const value = isString(e) ? e : e.target.value
    setValue('email', value)
  }

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const value = isString(e) ? e : e.target.value
    setValue('password', value)
    if (validatePasswordLive) {
      triggerValidation('password')
    }
  }

  const handleSubmit = async (data: SignupFormFields) => {
    setSubmitting(true)

    const trackingData = {
      eventLocation: ssoSignupState.authenticationCompleted
        ? track.EventLocation.SIGN_UP_MINUS_NAME_CONFIRMATION
        : track.EventLocation.SIGN_UP_MINUS_START,
      userAuthenticationMethod: ssoSignupState.authenticationCompleted
        ? ssoSignupState.provider
        : track.UserAuthenticationMethod.EMAIL,
    }

    const token = await handleReCaptchaVerify()
    const submitData = {
      formData: data,
      recaptchaToken: token,
    }

    onSubmit(submitData, trackingData)
      .then(onSubmitSuccess)
      .catch((resp) => {
        setSubmitting(false)
        if (resp._error === 'An Account Exists With This Email Address') {
          PubSub.publish(SET_UNIVERSAL_NOTIFICATION_MODAL_CONFIG_EVENT, {
            renderContent: (cm: () => void) => (
              <InfoModal
                closeModal={cm}
                email={data.email}
                type="existing"
                provider={'Email'}
              />
            ),
          })

          PubSub.publish(SHOW_UNIVERSAL_NOTIFICATION_MODAL_EVENT)
        } else {
          setFormSubmissionError(resp._error)
        }
      })
  }

  const handleBabylistResponse = (
    provider: 'Apple' | 'Google',
    response: any,
    setError: (error: string) => void
  ) => {
    if (response.user || response.isExistingUser === true) {
      track.signUpSubmitted({
        eventLocation: track.EventLocation.SIGN_UP_MINUS_START,
        eventType: track.EventType.ERROR_MINUS_EXISTING_EMAIL,
        userAuthenticationMethod: provider,
      })
      const responseEmail =
        response.user?.email || response.missingData.user.email

      if (response.user) {
        window.currentUser = response.user
      }

      PubSub.publish(SET_UNIVERSAL_NOTIFICATION_MODAL_CONFIG_EVENT, {
        renderContent: (cm: () => void) => (
          <InfoModal
            closeModal={cm}
            email={responseEmail}
            type="existing"
            provider={provider}
          />
        ),
      })

      PubSub.publish(SHOW_UNIVERSAL_NOTIFICATION_MODAL_EVENT)
    } else if (response.oktaRedirect) {
      window.location.href = response.oktaRedirect
    } else if (response.isExistingUser === false) {
      track.signUpSubmitted({
        eventLocation: track.EventLocation.SIGN_UP_MINUS_START,
        eventType: track.EventType.SUCCESSFUL,
        userAuthenticationMethod: provider,
      })

      setSsoSignupState({
        authenticationCompleted: true,
        email: response.missingData.user.email,
        firstName: response.missingData.user.firstName,
        isPrivateEmail: response.isPrivateEmail,
        lastName: response.missingData.user.lastName,
        provider,
      })

      setHeaderState({
        subSubTitle: true,
        subTitle: response.missingData.user.email,
        title: response.missingData.user.firstName
          ? `Welcome, ${response.missingData.user.firstName}!`
          : 'Welcome!',
      })
    } else {
      track.signUpSubmitted({
        eventLocation: track.EventLocation.SIGN_UP_MINUS_START,
        eventType: track.EventType.ERROR_MINUS_INVALID_CREDENTIALS,
        userAuthenticationMethod: provider,
      })

      setError(response.error)
    }
  }

  useEffect(() => {
    if (ssoSignupState.authenticationCompleted) {
      setValue('email', ssoSignupState.email)
      setValue('firstName', ssoSignupState.firstName)
      setValue('lastName', ssoSignupState.lastName)

      track.signUpStepProcessed({
        eventLocation: track.EventLocation.SIGN_UP_MINUS_NAME_CONFIRMATION,
        userAuthenticationMethod: ssoSignupState.provider,
      })
    }
  }, [ssoSignupState])

  const handleGoogleButtonClickTracking = () => {
    track.signUpStepProcessed({
      eventLocation: track.EventLocation.SIGN_UP_MINUS_START,
      userAuthenticationMethod: track.UserAuthenticationMethod.GOOGLE,
    })
  }

  const handleAppleButtonClickTracking = () => {
    track.signUpStepProcessed({
      eventLocation: track.EventLocation.SIGN_UP_MINUS_START,
      userAuthenticationMethod: track.UserAuthenticationMethod.APPLE,
    })
  }

  return (
    <Form error={formSubmissionError} onSubmit={rhfHandleSubmit(handleSubmit)}>
      {!ssoSignupState.authenticationCompleted && (
        <>
          {appleSsoEnabled && (
            <AppleSignInButton
              clickTracking={handleAppleButtonClickTracking}
              handleBabylistResponse={handleBabylistResponse}
              type="sign-up"
            />
          )}
          {ssoEnabled && (
            <GoogleSignInButton
              clickTracking={handleGoogleButtonClickTracking}
              handleBabylistResponse={handleBabylistResponse}
              label="Continue With Google"
            />
          )}
          {(ssoEnabled || appleSsoEnabled) && <SquiggleDivider />}
        </>
      )}
      <div className={styles.SignupForm__nameContainer}>
        <Input
          disabled={signedUp}
          error={
            errors.firstName &&
            generateErrorMessage('first name', errors.firstName)
          }
          groupClassName={styles.SignupForm__nameInput}
          id="firstName"
          inputRef={register({ required: true })}
          label="First Name"
          name="firstName"
          type="text"
        />
        <Input
          disabled={signedUp}
          error={
            errors.lastName &&
            generateErrorMessage('last name', errors.lastName)
          }
          groupClassName={styles.SignupForm__nameInput}
          id="lastName"
          inputRef={register({ required: true })}
          label="Last Name"
          name="lastName"
          type="text"
        />
      </div>
      {!ssoSignupState.authenticationCompleted && (
        <>
          <MailcheckedInput
            mailcheckOnChange
            autoComplete="email"
            disabled={signedUp}
            error={errors.email && generateErrorMessage('email', errors.email)}
            id="email"
            inputRef={register({ required: true, validate: email })}
            label="Email address"
            mailcheckDelay={300}
            mailcheckOnBlur={false}
            name="email"
            onChange={handleEmailChange}
          />
          <SecretInput
            autoCapitalize="none"
            autoComplete="off"
            autoCorrect="off"
            disabled={signedUp}
            error={
              errors.password &&
              generatePasswordErrorMessage(errors.password.type)
            }
            help={generatePasswordHelpText()}
            id="password"
            initiallyVisible={isMobile}
            inputRef={register({ required: true, minLength: 8 })}
            label="Password"
            name="password"
            onBlur={handlePasswordOnBlur}
            onChange={handlePasswordChange}
          />
        </>
      )}
      {ssoSignupState.authenticationCompleted && (
        <Input
          inputRef={register({ required: true, validate: email })}
          name="email"
          type="hidden"
          value={ssoSignupState.email}
        />
      )}
      {!signedUp && (
        <>
          {!ssoSignupState.isPrivateEmail && (
            <Checkbox
              inputRef={register()}
              label="I would like to receive promotional offers and updates from approved Babylist partners."
              name="consentedToPromotionalOffers"
            />
          )}
          <>
            {children}
            <div className="btn-group btn-group--stretch">
              <InfoButton
                // DO NOT REMOVE: Allows 3rd party tracking pixels to identify when this button is clicked
                block
                pill
                className="mbl"
                data-external-tracking-identifier="signup"
                disabled={signedUp || submitting}
                submitting={submitting}
                type="submit"
              >
                {submitButtonText}
              </InfoButton>
            </div>

            <p className="small text-center mbl">
              <i className="fa fa-lock" /> Your registry is private until you
              choose to share it.
            </p>
            <p className="h6 text-center text-muted">
              By clicking {submitButtonText} you agree to the
              <br className="visible-xs" /> Babylist{' '}
              <Link newWindow url={termsOfUsePath}>
                terms of use
              </Link>{' '}
              and{' '}
              <Link newWindow url={privacyPolicyPath}>
                privacy policy
              </Link>
              .
            </p>
            <p className="h6 text-center text-muted">
              This site is protected by reCAPTCHA and the Google
              <a href="https://policies.google.com/privacy">
                Privacy Policy
              </a>{' '}
              and
              <a href="https://policies.google.com/terms">
                Terms of Service
              </a>{' '}
              apply.
            </p>
          </>
        </>
      )}
    </Form>
  )
}

export default SignupForm
